import { prefersReducedMotion } from '@theme/utilities';

/**
 * TC: Mobile drawer autoplay carousel controller.
 *
 * Shared by both "Community photos" and "Best Sellers" bottom-content
 * modes in the redesigned mobile drawer (Sep 5 build). Wraps an existing,
 * natively-scrollable list (`[data-carousel-track]`) without replacing any
 * of its native scroll/touch/keyboard/scroll-snap behavior -- autoplay is
 * layered on top by nudging `scrollLeft` on a requestAnimationFrame loop.
 *
 * Markup contract:
 *   <tc-drawer-carousel data-autoplay="true|false" data-speed="25">
 *     <ul data-carousel-track>...items...</ul>
 *     <button data-carousel-playpause aria-pressed="false">
 *       <span data-carousel-playpause-icon aria-hidden="true"></span>
 *       <span data-carousel-playpause-label>Pause auto-scroll</span>
 *     </button>
 *   </tc-drawer-carousel>
 *
 * @extends HTMLElement
 */

/** Milliseconds of inactivity after a manual interaction before autoplay resumes. */
const RESUME_DELAY_MS = 3000;

/** Largest single animation-frame delta (seconds) allowed, to avoid a big
 * jump after the tab was backgrounded or the debugger was paused. */
const MAX_FRAME_DELTA = 0.25;

class TcDrawerCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #playPauseButton = null;

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {number | null} */
  #lastTimestamp = null;

  /** @type {number | null} */
  #resumeTimeoutId = null;

  /** Configured px/second scroll speed. @type {number} */
  #speed = 20;

  /** Whether autoplay is configured (setting enabled + more than one item + motion allowed). @type {boolean} */
  #autoplayConfigured = false;

  /** Permanently toggled by the user's play/pause control. @type {boolean} */
  #userPaused = false;

  /** Temporarily set during/just after manual scroll interaction. @type {boolean} */
  #tempPaused = false;

  /** Set while the pointer is hovering the carousel. @type {boolean} */
  #hoverPaused = false;

  /** Set while an item inside the track holds keyboard focus. @type {boolean} */
  #focusPaused = false;

  /** Width (px) of a single, non-duplicated set of items. @type {number} */
  #singleSetWidth = 0;

  connectedCallback() {
    const track = this.querySelector('[data-carousel-track]');
    if (!(track instanceof HTMLElement)) return;
    this.#track = track;
    this.#playPauseButton = this.querySelector('[data-carousel-playpause]');

    // Idempotent re-init: the Shopify Theme Editor can reconnect this
    // element (block re-render) without a full page reload. Strip any
    // clones left over from a previous connection before doing anything.
    this.#track.querySelectorAll('[data-tc-clone="true"]').forEach((node) => node.remove());
    this.#track.classList.remove('tc-drawer-carousel__track--autoplaying');

    this.#speed = Number(this.dataset.speed) || 20;
    this.#autoplayConfigured = this.dataset.autoplay === 'true';
    this.#dialog = this.closest('dialog');

    const itemCount = this.#track.children.length;
    const canAutoplay = this.#autoplayConfigured && itemCount > 1 && !prefersReducedMotion();

    if (this.#playPauseButton) {
      if (canAutoplay) {
        this.#playPauseButton.addEventListener('click', this.#handlePlayPauseClick);
      } else {
        // Reduced motion, a single item, or autoplay disabled -- nothing
        // for the control to do.
        this.#playPauseButton.hidden = true;
      }
    }

    if (!canAutoplay) return;

    this.#cloneItems();
    this.#bindInteractionPauses();
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#bindDialogObserver();
    this.#evaluate();
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
    this.#clearResumeTimer();

    if (this.#playPauseButton) {
      this.#playPauseButton.removeEventListener('click', this.#handlePlayPauseClick);
    }

    if (this.#track) {
      this.#track.querySelectorAll('[data-tc-clone="true"]').forEach((node) => node.remove());
      this.#track.removeEventListener('pointerdown', this.#handleDragStart);
      this.#track.removeEventListener('touchstart', this.#handleDragStart);
      this.#track.removeEventListener('pointerup', this.#handleDragEnd);
      this.#track.removeEventListener('pointercancel', this.#handleDragEnd);
      this.#track.removeEventListener('touchend', this.#handleDragEnd);
      this.#track.removeEventListener('touchcancel', this.#handleDragEnd);
      this.#track.removeEventListener('wheel', this.#handleWheel);
      this.#track.removeEventListener('focusin', this.#handleFocusIn);
      this.#track.removeEventListener('focusout', this.#handleFocusOut);
      this.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.removeEventListener('mouseleave', this.#handleMouseLeave);
    }
  }

  /**
   * Duplicate every original (non-clone) item once (for a seamless infinite
   * loop) and mark the clones fully inert: hidden from assistive tech,
   * unreachable by keyboard, and stripped of any id so nothing in the DOM
   * is duplicated. Safe to call more than once -- always strips any
   * existing clones first, so it never compounds.
   */
  #cloneItems() {
    if (!this.#track) return;
    this.#track.querySelectorAll('[data-tc-clone="true"]').forEach((node) => node.remove());
    const originalChildren = Array.from(this.#track.children);

    for (const child of originalChildren) {
      const clone = /** @type {HTMLElement} */ (child.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.setAttribute('data-tc-clone', 'true');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      this.#track.appendChild(clone);
    }

    // Measure once both copies are in the DOM -- half the total scrollWidth
    // is the width of a single set, used to wrap scrollLeft seamlessly.
    // Only trust this measurement while the track actually has layout (the
    // drawer dialog is open); otherwise scrollWidth reads 0 and would
    // corrupt the wrap math.
    if (this.#isDialogOpen()) {
      this.#singleSetWidth = this.#track.scrollWidth / 2;
    }
  }

  /**
   * Re-clone if something external stripped our runtime-injected clones
   * without going through a full custom-element reconnect. This theme's
   * Section Rendering API can morph a section's markup back to its
   * server-rendered (un-cloned) state in place, preserving this custom
   * element's identity -- which means connectedCallback never re-fires,
   * so without this check the seamless-loop duplicate set silently
   * disappears the first time that happens. Only runs while the drawer
   * is open, so the scrollWidth measurement inside #cloneItems() is valid.
   */
  #ensureClones() {
    if (!this.#track || !this.#autoplayConfigured) return;
    if (!this.#isDialogOpen()) return;
    if (this.#track.querySelector('[data-tc-clone="true"]')) return;
    if (this.#track.children.length < 2) return;
    this.#cloneItems();
  }

  #bindInteractionPauses() {
    if (!this.#track) return;
    this.#track.addEventListener('pointerdown', this.#handleDragStart, { passive: true });
    this.#track.addEventListener('touchstart', this.#handleDragStart, { passive: true });
    this.#track.addEventListener('pointerup', this.#handleDragEnd, { passive: true });
    this.#track.addEventListener('pointercancel', this.#handleDragEnd, { passive: true });
    this.#track.addEventListener('touchend', this.#handleDragEnd, { passive: true });
    this.#track.addEventListener('touchcancel', this.#handleDragEnd, { passive: true });
    this.#track.addEventListener('wheel', this.#handleWheel, { passive: true });
    this.#track.addEventListener('focusin', this.#handleFocusIn);
    this.#track.addEventListener('focusout', this.#handleFocusOut);
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
  }

  #bindDialogObserver() {
    if (!this.#dialog) return; // Not inside a <dialog> -- treat as always "open".
    this.#dialogObserver = new MutationObserver(() => this.#evaluate());
    this.#dialogObserver.observe(this.#dialog, { attributes: true, attributeFilter: ['open'] });
  }

  #handleVisibilityChange = () => this.#evaluate();

  #handleMouseEnter = () => {
    this.#hoverPaused = true;
    this.#evaluate();
  };

  #handleMouseLeave = () => {
    this.#hoverPaused = false;
    this.#evaluate();
  };

  #handleFocusIn = () => {
    this.#focusPaused = true;
    this.#evaluate();
  };

  #handleFocusOut = () => {
    this.#focusPaused = false;
    this.#evaluate();
  };

  /** Start of a touch/pointer drag (swipe) -- pause immediately. */
  #handleDragStart = () => {
    this.#tempPaused = true;
    this.#clearResumeTimer();
    this.#evaluate();
  };

  /** End of a touch/pointer drag -- schedule resume after the delay. */
  #handleDragEnd = () => {
    this.#scheduleResume();
  };

  /** Mouse-wheel / trackpad scroll -- debounce: keep pausing and pushing
   * the resume timer back while wheel events keep arriving. */
  #handleWheel = () => {
    this.#tempPaused = true;
    this.#scheduleResume();
    this.#evaluate();
  };

  #clearResumeTimer() {
    if (this.#resumeTimeoutId !== null) {
      clearTimeout(this.#resumeTimeoutId);
      this.#resumeTimeoutId = null;
    }
  }

  #scheduleResume() {
    this.#clearResumeTimer();
    this.#resumeTimeoutId = window.setTimeout(() => {
      this.#tempPaused = false;
      this.#resumeTimeoutId = null;
      this.#evaluate();
    }, RESUME_DELAY_MS);
  }

  #handlePlayPauseClick = () => {
    this.#userPaused = !this.#userPaused;

    if (this.#playPauseButton) {
      this.#playPauseButton.setAttribute('aria-pressed', String(this.#userPaused));
      this.#playPauseButton.classList.toggle('tc-drawer-carousel__playpause--paused', this.#userPaused);
      const label = this.#playPauseButton.querySelector('[data-carousel-playpause-label]');
      if (label) {
        label.textContent = this.#userPaused ? 'Resume auto-scroll' : 'Pause auto-scroll';
      }
    }

    this.#evaluate();
  };

  #isDialogOpen() {
    if (!this.#dialog) return true;
    return this.#dialog.hasAttribute('open');
  }

  #shouldRun() {
    return (
      this.#autoplayConfigured &&
      !this.#userPaused &&
      !this.#tempPaused &&
      !this.#hoverPaused &&
      !this.#focusPaused &&
      !document.hidden &&
      this.#isDialogOpen()
    );
  }

  #evaluate() {
    this.#ensureClones();

    if (this.#shouldRun()) {
      this.#start();
    } else {
      this.#stop();
    }
  }

  #start() {
    if (this.#rafId !== null || !this.#track) return;
    this.#track.classList.add('tc-drawer-carousel__track--autoplaying');
    this.#lastTimestamp = null;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    this.#lastTimestamp = null;
    this.#track?.classList.remove('tc-drawer-carousel__track--autoplaying');
  }

  /** @param {number} timestamp */
  #tick = (timestamp) => {
    if (!this.#track) return;

    if (this.#lastTimestamp === null) {
      this.#lastTimestamp = timestamp;
      this.#rafId = requestAnimationFrame(this.#tick);
      return;
    }

    let dt = (timestamp - this.#lastTimestamp) / 1000;
    this.#lastTimestamp = timestamp;
    if (dt > MAX_FRAME_DELTA) dt = MAX_FRAME_DELTA;

    if (this.#singleSetWidth > 0) {
      let next = this.#track.scrollLeft + this.#speed * dt;
      if (next >= this.#singleSetWidth) {
        next -= this.#singleSetWidth;
      }
      this.#track.scrollLeft = next;
    }

    this.#rafId = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('tc-drawer-carousel')) {
  customElements.define('tc-drawer-carousel', TcDrawerCarousel);
}
